/**
 * Seção principal (hero) com mockups do cardápio e do painel
 */

import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  PlayCircle,
  QrCode,
  Sparkles,
  CheckCircle2,
  Bike,
  ChefHat,
  Clock,
  Plus,
  Search,
} from 'lucide-react';
import { INTEGRATIONS } from '../content';
import type { SegmentMockup } from '../segments';

const DEFAULT_MOCKUP: SegmentMockup = {
  storeName: 'Pizzaria Bella Massa',
  emoji: '🍕',
  status: 'Aberto · 30-45 min',
  categories: ['Pizzas', 'Burgers', 'Bebidas', 'Doces'],
  items: [
    {
      name: 'Pizza Calabresa',
      desc: 'Molho, mussarela, calabresa e cebola',
      price: 'R$ 54,90',
      emoji: '🍕',
    },
    { name: 'Meio a meio', desc: 'Escolha 2 sabores', price: 'R$ 62,90', emoji: '🍕' },
    {
      name: 'Smash Burger',
      desc: 'Pão brioche, 2 carnes, cheddar',
      price: 'R$ 32,90',
      emoji: '🍔',
    },
    { name: 'Refrigerante 2L', desc: 'Gelado', price: 'R$ 14,00', emoji: '🥤' },
  ],
  cartLabel: 'Ver carrinho · 2 itens',
  cartTotal: 'R$ 117,80',
};

/** Celular com a loja online (conteúdo ilustrativo, configurável por segmento) */
export function PhoneMockup({ mockup = DEFAULT_MOCKUP }: { mockup?: SegmentMockup }) {
  return (
    // vw-keep: o mockup mantém as cores escuras também no tema claro do site
    <div className="vw-keep relative mx-auto w-[260px] sm:w-[290px]">
      <div className="rounded-[2.6rem] border border-white/15 bg-gradient-to-b from-slate-700 to-slate-900 p-2.5 shadow-2xl shadow-violet-950/60">
        <div className="overflow-hidden rounded-[2.1rem] bg-white">
          {/* Capa da loja */}
          <div className="relative h-24 bg-gradient-to-br from-violet-700 via-fuchsia-600 to-orange-400">
            <div className="absolute left-1/2 top-2 h-5 w-24 -translate-x-1/2 rounded-full bg-black/80" />
            <div className="absolute -bottom-6 left-4 flex h-12 w-12 items-center justify-center rounded-2xl border-4 border-white bg-slate-900 text-xl">
              {mockup.emoji}
            </div>
          </div>
          <div className="px-4 pb-4 pt-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-900">{mockup.storeName}</p>
                <p className="flex items-center gap-1 text-[11px] text-emerald-700">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{' '}
                  {mockup.status}
                </p>
              </div>
              <span className="rounded-full bg-slate-100 p-1.5 text-slate-500">
                <Search size={12} />
              </span>
            </div>
            <div className="mt-3 flex gap-1.5 overflow-hidden text-[10px] font-semibold">
              {mockup.categories.map((c, i) => (
                <span
                  key={c}
                  className={`shrink-0 rounded-full px-2.5 py-1 ${i === 0 ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {c}
                </span>
              ))}
            </div>
            <ul className="mt-3 space-y-2">
              {mockup.items.map(item => (
                <li
                  key={item.name}
                  className="flex items-center gap-2.5 rounded-xl border border-slate-100 p-2"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-lg">
                    {item.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-semibold text-slate-900">{item.name}</p>
                    <p className="truncate text-[10px] text-slate-500">{item.desc}</p>
                    <p className="text-[11px] font-bold text-violet-700">{item.price}</p>
                  </div>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-600 text-white">
                    <Plus size={12} />
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-violet-600 px-3 py-2.5 text-white">
              <span className="text-[11px] font-semibold">{mockup.cartLabel}</span>
              <span className="text-[11px] font-bold">{mockup.cartTotal}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrdersPanelMockup() {
  const columns = [
    {
      title: 'Novos',
      color: 'bg-sky-400',
      orders: [{ id: '#1042', who: 'Mesa 7', total: 'R$ 89,90', icon: QrCode }],
    },
    {
      title: 'Preparando',
      color: 'bg-amber-400',
      orders: [{ id: '#1041', who: 'Delivery', total: 'R$ 117,80', icon: ChefHat }],
    },
    {
      title: 'Saiu p/ entrega',
      color: 'bg-emerald-400',
      orders: [{ id: '#1039', who: 'Entregador João', total: 'R$ 64,50', icon: Bike }],
    },
  ];
  return (
    <div className="vw-keep vw-glass w-[300px] rounded-2xl p-4 shadow-2xl shadow-black/40 sm:w-[340px]">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-white">Pedidos em tempo real</p>
        <span className="flex items-center gap-1.5 text-[11px] text-emerald-300">
          <span className="relative flex h-2 w-2">
            <span className="vw-ping-dot absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          Ao vivo
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {columns.map(col => (
          <div key={col.title} className="rounded-xl bg-white/5 p-2">
            <p className="flex items-center gap-1 text-[10px] font-medium text-slate-300">
              <span className={`h-1.5 w-1.5 rounded-full ${col.color}`} /> {col.title}
            </p>
            {col.orders.map(o => (
              <div key={o.id} className="mt-2 rounded-lg bg-slate-900/80 p-2">
                <p className="flex items-center justify-between text-[10px] font-bold text-white">
                  {o.id} <o.icon size={10} className="text-violet-300" />
                </p>
                <p className="truncate text-[9px] text-slate-400">{o.who}</p>
                <p className="text-[10px] font-semibold text-violet-200">{o.total}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Hero({ trialDays }: { trialDays: number }) {
  return (
    <section
      id="inicio"
      className="relative isolate overflow-hidden pb-20 pt-28 sm:pt-36 lg:pb-28 lg:pt-40"
    >
      {/* Fundo tecnológico */}
      <div className="vw-grid absolute inset-0 -z-10" aria-hidden />
      <div
        className="vw-orb -z-10 left-[-10%] top-[-10%] h-[420px] w-[420px] bg-violet-700"
        aria-hidden
      />
      <div
        className="vw-orb -z-10 right-[-8%] top-[20%] h-[380px] w-[380px] bg-fuchsia-700/70"
        aria-hidden
      />
      <div
        className="vw-orb -z-10 bottom-[-20%] left-[30%] h-[300px] w-[500px] bg-indigo-700/60"
        aria-hidden
      />

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8">
        <div className="text-center lg:text-left">
          <span className="vw-gradient-border inline-flex items-center gap-2 rounded-full bg-slate-900/70 px-4 py-1.5 text-xs font-medium text-violet-200 sm:text-sm">
            <Sparkles size={14} className="text-fuchsia-300" />
            Chega de WhatsApp lotado e comissão de marketplace
          </span>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.25rem] xl:text-6xl">
            Seu delivery e seu salão em <span className="vw-gradient-text">um só sistema</span>.
            <br className="hidden sm:block" /> Sem taxa por pedido.
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg lg:mx-0">
            Cardápio digital com link próprio, robô que fecha pedidos no WhatsApp, QR Code na mesa,
            PIX confirmado direto no seu banco e gestão de estoque e financeiro. Para pizzarias,
            hamburguerias, restaurantes, padarias, barbearias e muito mais.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
            <Link
              to="/cadastro"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 bg-[length:200%_auto] px-6 py-4 sm:whitespace-nowrap text-base font-semibold text-white shadow-xl shadow-violet-900/50 transition-all hover:bg-right sm:w-auto"
            >
              Começar teste grátis de {trialDays} dias
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#dores"
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-6 py-4 sm:whitespace-nowrap text-base font-semibold text-white backdrop-blur transition hover:bg-white/10 sm:w-auto"
            >
              <PlayCircle size={18} /> Ver como funciona
            </a>
          </div>

          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-400 lg:justify-start">
            {['Sem cartão de crédito', 'Sem fidelidade', 'Loja pronta em minutos'].map(t => (
              <li key={t} className="flex items-center gap-1.5">
                <Check size={16} className="text-emerald-400" /> {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Mockups */}
        <div className="relative mx-auto flex w-full max-w-lg justify-center lg:justify-end">
          <div className="vw-float relative z-10">
            <PhoneMockup />
          </div>
          <div className="vw-float-delay absolute -bottom-10 -left-2 z-20 hidden sm:block lg:-left-16">
            <OrdersPanelMockup />
          </div>
          <div className="vw-float absolute -right-2 top-6 z-20 hidden items-center gap-3 rounded-2xl border border-emerald-400/20 bg-slate-900/90 px-4 py-3 shadow-xl sm:flex lg:-right-6">
            <CheckCircle2 className="text-emerald-400" size={22} />
            <div>
              <p className="text-xs font-semibold text-white">PIX aprovado</p>
              <p className="text-[11px] text-slate-400">Pedido #1042 · R$ 89,90</p>
            </div>
          </div>
          <div className="vw-float-delay absolute left-0 top-24 z-20 hidden items-center gap-3 rounded-2xl border border-violet-400/20 bg-slate-900/90 px-4 py-3 shadow-xl md:flex lg:-left-10">
            <Clock className="text-violet-300" size={20} />
            <div>
              <p className="text-xs font-semibold text-white">Mesa 7 fez um pedido</p>
              <p className="text-[11px] text-slate-400">via QR Code · agora</p>
            </div>
          </div>
        </div>
      </div>

      <IntegrationsStrip />
    </section>
  );
}

/** Faixa animada com as integrações */
export function IntegrationsStrip() {
  return (
    <div className="mx-auto mt-24 max-w-6xl px-4 sm:px-6 lg:px-8">
      <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        Integrado ao que o seu negócio já usa
      </p>
      <div className="relative mt-6 overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
        <div className="vw-marquee flex w-max gap-4">
          {[...INTEGRATIONS, ...INTEGRATIONS].map((item, i) => (
            <span
              key={`${item.label}-${i}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-slate-300"
              aria-hidden={i >= INTEGRATIONS.length}
            >
              <item.icon size={16} className="text-violet-300" /> {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
